import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MDButton from "components/MDButton";
import FormField from "layouts/pages/account/components/FormField";
import { getAllEventosPeridoByActive } from "services/evento";
import type { IEventoResponse } from "interfaces/eventos";
import { sugerirImpostoContaPagar } from "../services/contas-pagar.service";
import { resolverCentroCustoIdEvento } from "../helpers/resolver-centro-custo-evento";
import type { NovaContaPagarForm } from "../hooks/useCriarTituloPagar";
import type { CentroCustoApi } from "../types/centro-custo.types";
import type { FornecedorFinanceiroApi } from "../types/contas-pagar.types";
import { formatarMoedaBRL } from "../helpers/financeiro-formatters";
import FornecedorAutocompleteField from "./FornecedorAutocompleteField";
import { currencyMaskBRL, currencyToNumber } from "helpers/utils";
import {
  fieldSx,
  modalContentSx,
  modalHeaderSx,
  modalPaperWideSx,
  saveButtonSx,
} from "../../components/contas-financeiras.theme";

type OpcaoStr = { value: string; label: string };

const INPUT_HEIGHT = 44;
const inputProps = { style: { height: INPUT_HEIGHT } };
const fieldProps = { InputProps: inputProps, sx: fieldSx };

const OPCOES_NATUREZA: OpcaoStr[] = [
  { value: "VARIAVEL", label: "Variável" },
  { value: "FIXA", label: "Fixa" },
];

const OPCOES_CATEGORIA: OpcaoStr[] = [
  { value: "INFRAESTRUTURA", label: "Infraestrutura" },
  { value: "SERVICOS", label: "Serviços" },
  { value: "EVENTOS", label: "Eventos" },
  { value: "FORNECEDORES", label: "Fornecedores" },
  { value: "IMPOSTOS", label: "Impostos" },
  { value: "MARKETING", label: "Marketing" },
  { value: "FRETE", label: "Frete" },
  { value: "OPERACIONAL", label: "Operacional" },
  { value: "OUTROS", label: "Outros" },
];

const OPCOES_CONTEXTO: OpcaoStr[] = [
  { value: "LOJA", label: "Loja" },
  { value: "EVENTO", label: "Evento" },
  { value: "ADMINISTRATIVO", label: "Administrativo" },
  { value: "OPERACIONAL", label: "Operacional" },
  { value: "OUTROS", label: "Outros" },
];

export type NovaContaPagarDialogProps = {
  open: boolean;
  form: NovaContaPagarForm;
  setForm: Dispatch<SetStateAction<NovaContaPagarForm>>;
  loading: boolean;
  error: string | null;
  centros: CentroCustoApi[];
  fornecedores: FornecedorFinanceiroApi[];
  onRefetchFornecedores?: () => void;
  onClose: () => void;
  onConfirm: () => void;
  /** create = nova conta; edit = alterar título pendente */
  variant?: "create" | "edit";
  /** Cabeçalho com imagem do evento (ex.: despesas do evento). */
  eventoHeader?: {
    titulo: string;
    urlImageDestaque?: string;
  };
};

export default function NovaContaPagarDialog({
  open,
  form,
  setForm,
  loading,
  error,
  centros,
  fornecedores,
  onRefetchFornecedores,
  onClose,
  onConfirm,
  variant = "create",
  eventoHeader,
}: NovaContaPagarDialogProps) {
  const isEdit = variant === "edit";
  const contextoEventoFixo = Boolean(eventoHeader);
  const [eventos, setEventos] = useState<IEventoResponse[]>([]);
  const [eventosLoading, setEventosLoading] = useState(false);
  const [sugerindoImposto, setSugerindoImposto] = useState(false);
  const [impostoInfo, setImpostoInfo] = useState<string | null>(null);

  const isImposto = form.categoria === "IMPOSTOS";
  const exigeEvento = form.contexto === "EVENTO";

  useEffect(() => {
    if (!open || !contextoEventoFixo) return;
    setForm((prev) =>
      prev.contexto === "EVENTO" ? prev : { ...prev, contexto: "EVENTO" },
    );
  }, [open, contextoEventoFixo, setForm]);

  useEffect(() => {
    if (!open || contextoEventoFixo) return;
    let cancelled = false;
    const run = async () => {
      setEventosLoading(true);
      try {
        const res = await getAllEventosPeridoByActive(1, 200);
        if (!cancelled) setEventos(res.eventos ?? []);
      } catch {
        if (!cancelled) setEventos([]);
      } finally {
        if (!cancelled) setEventosLoading(false);
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [open, contextoEventoFixo]);

  useEffect(() => {
    if (!open || form.contexto !== "EVENTO" || form.eventoId == null) return;

    const match = centros.find((c) => c.eventoId === form.eventoId);
    if (match) {
      setForm((prev) =>
        prev.centroCustoId.trim()
          ? prev
          : { ...prev, centroCustoId: match.id },
      );
      return;
    }

    let cancelled = false;
    void resolverCentroCustoIdEvento(form.eventoId, centros).then((id) => {
      if (cancelled || !id) return;
      setForm((prev) =>
        prev.centroCustoId.trim() ? prev : { ...prev, centroCustoId: id },
      );
    });
    return () => {
      cancelled = true;
    };
  }, [open, form.contexto, form.eventoId, centros, setForm]);

  const opcoesCentro = useMemo(() => {
    let lista = centros;
    if (form.contexto === "EVENTO" && form.eventoId != null) {
      lista = centros.filter(
        (c) => c.eventoId === form.eventoId || c.tipo !== "EVENTO",
      );
    }
    const base = lista.map((c) => ({ value: c.id, label: c.nome }));
    const id = form.centroCustoId.trim();
    if (id && !base.some((o) => o.value === id)) {
      const ev = eventos.find((e) => e.id === form.eventoId);
      base.push({
        value: id,
        label: ev?.titulo
          ? `Evento — ${ev.titulo}`
          : eventoHeader?.titulo
            ? `Evento — ${eventoHeader.titulo}`
            : "Centro do evento",
      });
    }
    return base;
  }, [centros, form.centroCustoId, form.eventoId, form.contexto, eventos, eventoHeader?.titulo]);
  const opcoesEvento = useMemo(
    () =>
      eventos.map((e) => ({
        value: e.id,
        label: e.titulo ?? `Evento #${e.id}`,
      })),
    [eventos],
  );

  const centroSel =
    opcoesCentro.find((o) => o.value === form.centroCustoId) ?? null;
  const eventoSel =
    opcoesEvento.find((o) => o.value === form.eventoId) ?? null;

  const patch =
    (field: keyof NovaContaPagarForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const v = e.target.value;
      setForm((prev) => {
        const next = { ...prev, [field]: v };
        if (field === "categoria" && v === "IMPOSTOS") {
          next.contexto = prev.contexto === "OUTROS" ? "EVENTO" : prev.contexto;
        }
        if (field === "contexto" && v !== "EVENTO") {
          next.eventoId = null;
        }
        return next;
      });
    };

  /** Armazena centavos em dígitos — compatível com `currencyMaskBRL` / `currencyToNumber`. */
  const patchMoeda =
    (field: "valorTotal" | "baseCalculoImposto") =>
    (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const digits = (e.target.value || "").replace(/\D/g, "");
      setForm((prev) => ({ ...prev, [field]: digits }));
    };

  const valorImpostoCalculado = useMemo(() => {
    if (!isImposto) return null;
    const base = currencyToNumber(form.baseCalculoImposto);
    const ali = Number(form.aliquotaImposto.replace(",", "."));
    if (!Number.isFinite(base) || !Number.isFinite(ali) || base <= 0) return null;
    return Math.round(base * ali) / 100;
  }, [form.baseCalculoImposto, form.aliquotaImposto, isImposto]);

  useEffect(() => {
    if (isImposto && valorImpostoCalculado != null) {
      setForm((prev) => ({
        ...prev,
        valorTotal: String(Math.round(valorImpostoCalculado * 100)),
      }));
    }
  }, [valorImpostoCalculado, isImposto, setForm]);

  const handleSugerirImposto = useCallback(async () => {
    setImpostoInfo(null);
    const ali = Number(form.aliquotaImposto.replace(",", "."));
    if (!Number.isFinite(ali) || ali < 6 || ali > 13.2) {
      setImpostoInfo("Informe uma alíquota entre 6% e 13,2%.");
      return;
    }
    const usaReceitaTotalEvento = form.eventoId != null;
    const compIni = form.competenciaEm || form.vencimento;
    const compFim = form.competenciaEm || form.vencimento;
    if (!usaReceitaTotalEvento && !compIni) {
      setImpostoInfo("Informe a competência ou vencimento.");
      return;
    }
    setSugerindoImposto(true);
    try {
      const { data, status } = await sugerirImpostoContaPagar({
        eventoId: form.eventoId ?? undefined,
        ...(usaReceitaTotalEvento
          ? {}
          : {
              competenciaInicio: compIni,
              competenciaFim: compFim,
            }),
        aliquota: ali,
      });
      if (status !== 200 || !data) {
        setImpostoInfo("Não foi possível calcular a sugestão.");
        return;
      }
      setForm((prev) => ({
        ...prev,
        baseCalculoImposto: String(Math.round(data.baseCalculo * 100)),
        valorTotal: String(Math.round(data.valorSugerido * 100)),
        aliquotaImposto: String(data.aliquota),
      }));
      const fonteLabel =
        data.fonte === "receita_evento"
          ? "receita total do evento (inscrições pagas)"
          : data.fonte === "receita_loja"
            ? "receita consolidada da loja"
            : "preenchimento manual";
      setImpostoInfo(
        `Base sugerida: ${formatarMoedaBRL(data.baseCalculo)} (${fonteLabel}). Valor: ${formatarMoedaBRL(data.valorSugerido)}.`,
      );
    } catch {
      setImpostoInfo("Erro ao buscar sugestão de imposto.");
    } finally {
      setSugerindoImposto(false);
    }
  }, [form, setForm]);

  const podeConfirmar =
    !loading &&
    Boolean(form.descricao.trim()) &&
    Boolean(form.vencimento.trim()) &&
    (!exigeEvento || form.eventoId != null) &&
    (isImposto
      ? Boolean(form.baseCalculoImposto.trim()) &&
        Boolean(form.aliquotaImposto.trim())
      : Boolean(form.valorTotal.trim()));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: modalPaperWideSx }}
    >
      <Box sx={modalHeaderSx}>
        <Stack direction="row" alignItems="center" spacing={2}>
          {eventoHeader?.urlImageDestaque ? (
            <Box
              component="img"
              src={eventoHeader.urlImageDestaque}
              alt={eventoHeader.titulo}
              sx={{
                width: 56,
                height: 56,
                borderRadius: 1.5,
                objectFit: "cover",
                border: "2px solid rgba(255,255,255,0.2)",
                flexShrink: 0,
              }}
            />
          ) : (
            <ReceiptLongOutlinedIcon sx={{ fontSize: 40, opacity: 0.9 }} />
          )}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ opacity: 0.75, letterSpacing: 1 }}>
              {eventoHeader
                ? isEdit
                  ? "Editar despesa"
                  : "Nova despesa"
                : isEdit
                  ? "Editar conta a pagar"
                  : "Nova conta a pagar"}
            </Typography>
            <Typography variant="subtitle1" fontWeight={700} noWrap>
              {eventoHeader?.titulo ?? "Conta a pagar"}
            </Typography>
            {!eventoHeader ? (
              <Typography variant="caption" sx={{ opacity: 0.8, display: "block", mt: 0.25 }}>
                {isEdit
                  ? "Altere valor, vencimento e demais dados do título."
                  : "Cadastre uma obrigação com vencimento e classificação."}
              </Typography>
            ) : null}
          </Box>
          <IconButton onClick={onClose} sx={{ color: "inherit" }} aria-label="Fechar">
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      <DialogContent sx={modalContentSx}>
        <Box sx={{ width: "100%", boxSizing: "border-box" }}>
          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : null}

          <Stack spacing={2}>
            <FormField
              label="Descrição"
              value={form.descricao}
              onChange={patch("descricao")}
              fullWidth
              required
              placeholder="Ex.: Água, Energia, Aluguel"
              {...fieldProps}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormField
                  select
                  label="Natureza"
                  value={form.naturezaDespesa}
                  onChange={patch("naturezaDespesa")}
                  fullWidth
                  {...fieldProps}
                >
                  {OPCOES_NATUREZA.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </FormField>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormField
                  select
                  label="Categoria"
                  value={form.categoria}
                  onChange={patch("categoria")}
                  fullWidth
                  {...fieldProps}
                >
                  {OPCOES_CATEGORIA.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </FormField>
              </Box>
              {!contextoEventoFixo ? (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormField
                    select
                    label="Contexto"
                    value={form.contexto}
                    onChange={patch("contexto")}
                    fullWidth
                    {...fieldProps}
                  >
                    {OPCOES_CONTEXTO.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </FormField>
                </Box>
              ) : null}
            </Stack>

            {exigeEvento && !contextoEventoFixo ? (
              <Autocomplete
                fullWidth
                options={opcoesEvento}
                value={eventoSel}
                loading={eventosLoading}
                onChange={(_, v) =>
                  setForm((prev) => ({
                    ...prev,
                    eventoId: v?.value ?? null,
                    contexto: "EVENTO",
                    centroCustoId: "",
                  }))
                }
                isOptionEqualToValue={(a, b) => a.value === b.value}
                renderInput={(params) => (
                  <FormField
                    {...params}
                    label="Evento"
                    required
                    placeholder="Selecione o evento"
                    sx={fieldSx}
                    InputProps={{
                      ...params.InputProps,
                      ...inputProps,
                    }}
                  />
                )}
              />
            ) : null}

            <FornecedorAutocompleteField
              fornecedores={fornecedores}
              value={form.fornecedorId.trim() ? form.fornecedorId : null}
              onChange={(id) =>
                setForm((prev) => ({ ...prev, fornecedorId: id ?? "" }))
              }
              onRefetchFornecedores={onRefetchFornecedores}
              label="Fornecedor (opcional)"
              disabled={loading}
            />

            <Autocomplete
              fullWidth
              options={opcoesCentro}
              value={centroSel}
              onChange={(_, v) =>
                setForm((prev) => ({ ...prev, centroCustoId: v?.value ?? "" }))
              }
              isOptionEqualToValue={(a, b) => a.value === b.value}
              renderInput={(params) => (
                <FormField
                  {...params}
                  label="Centro de custo (opcional)"
                  placeholder="Selecione"
                  sx={fieldSx}
                  InputProps={{
                    ...params.InputProps,
                    ...inputProps,
                  }}
                />
              )}
            />

            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormField
                  type="date"
                  label="Vencimento"
                  value={form.vencimento}
                  onChange={patch("vencimento")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  required
                  {...fieldProps}
                />
              </Box>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <FormField
                  type="date"
                  label="Competência"
                  value={form.competenciaEm}
                  onChange={patch("competenciaEm")}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  {...fieldProps}
                />
              </Box>
              {!isImposto ? (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <FormField
                    label="Valor total (R$)"
                    value={currencyMaskBRL(form.valorTotal ?? "0")}
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.select()
                    }
                    onChange={patchMoeda("valorTotal")}
                    fullWidth
                    placeholder="0,00"
                    required
                    {...fieldProps}
                  />
                </Box>
              ) : null}
            </Stack>

            {isImposto ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: "grey.200",
                  bgcolor: "grey.50",
                }}
              >
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Provisão de imposto
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{ mb: 2 }}
                >
                  Alíquota configurável entre 6% e 13,2%. Sugestão operacional —
                  não substitui o contador.
                </Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ width: "100%" }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormField
                      label="Base de cálculo (R$)"
                      value={currencyMaskBRL(form.baseCalculoImposto ?? "0")}
                      onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                        e.target.select()
                      }
                      onChange={patchMoeda("baseCalculoImposto")}
                      fullWidth
                      required
                      {...fieldProps}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormField
                      label="Alíquota (%)"
                      value={form.aliquotaImposto}
                      onChange={patch("aliquotaImposto")}
                      fullWidth
                      required
                      {...fieldProps}
                    />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <FormField
                      label="Valor do título (R$)"
                      value={currencyMaskBRL(form.valorTotal ?? "0")}
                      onChange={patchMoeda("valorTotal")}
                      fullWidth
                      helperText={
                        valorImpostoCalculado != null
                          ? `Calculado: ${formatarMoedaBRL(valorImpostoCalculado)}`
                          : undefined
                      }
                      {...fieldProps}
                    />
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  <MDButton
                    variant="outlined"
                    color="dark"
                    size="small"
                    onClick={() => void handleSugerirImposto()}
                    disabled={sugerindoImposto || loading}
                  >
                    {sugerindoImposto ? "Calculando…" : "Calcular pela receita"}
                  </MDButton>
                </Stack>
                {impostoInfo ? (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    {impostoInfo}
                  </Alert>
                ) : null}
              </Box>
            ) : null}

            <FormField
              label="Observação"
              value={form.observacao}
              onChange={patch("observacao")}
              fullWidth
              multiline
              minRows={3}
              maxRows={8}
              placeholder="Informações adicionais (opcional)"
              sx={fieldSx}
            />
          </Stack>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2.5, sm: 3 },
          py: 2,
          borderTop: "1px solid",
          borderColor: "grey.200",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        <MDButton variant="outlined" color="dark" onClick={onClose} disabled={loading}>
          Cancelar
        </MDButton>
        <MDButton
          variant="gradient"
          color="dark"
          onClick={onConfirm}
          disabled={!podeConfirmar}
          sx={saveButtonSx}
        >
          {loading
            ? "Salvando…"
            : isEdit
              ? "Salvar alterações"
              : "Criar título"}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}
